import { useEffect, useRef } from 'react';

import GalleryService from '@/services/gallery.service';

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_VIDEO_SIZE_BYTES = 500 * 1024 * 1024;
const TUS_CHUNK_SIZE = 5 * 1024 * 1024;

async function uploadWithTus(file, uploadURL, onProgress) {
  const offsetResponse = await fetch(uploadURL, {
    method: 'HEAD',
    headers: { 'Tus-Resumable': '1.0.0' },
  });
  if (!offsetResponse.ok) {
    throw new Error(`Video upload could not be resumed (${offsetResponse.status})`);
  }

  let offset = Number(offsetResponse.headers.get('Upload-Offset'));
  if (!Number.isFinite(offset)) throw new Error('Video upload returned no upload offset');
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + TUS_CHUNK_SIZE);
    const response = await fetch(uploadURL, {
      method: 'PATCH',
      headers: {
        'Tus-Resumable': '1.0.0',
        'Upload-Offset': String(offset),
        'Content-Type': 'application/offset+octet-stream',
      },
      body: chunk,
    });
    if (!response.ok) throw new Error(`Video upload failed (${response.status})`);
    offset = Number(response.headers.get('Upload-Offset'));
    if (!Number.isFinite(offset)) throw new Error('Video upload returned no upload offset');
    onProgress(Math.round((offset / file.size) * 100));
  }
}

export default function VideoManager({ setTriggerVideoUpload, dispatch, canUpload }) {
  const inputRef = useRef(null);

  const uploadVideo = async file => {
    if (!file) return;
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      alert('Please choose an MP4, WebM, or MOV video.');
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      alert('Videos must be 500 MB or smaller.');
      return;
    }
    if (!canUpload()) {
      alert('Only one media item can be added to a post.');
      return;
    }

    let assetId = null;
    let blockCreated = false;
    try {
      const { response, data } = await GalleryService.createPostVideoDirectUpload({
        fileSize: file.size,
        fileName: file.name,
        contentType: file.type,
      });
      if (!response.ok || !data?.assetId || !data?.uploadURL) {
        throw new Error('Unable to prepare the video upload');
      }
      assetId = data.assetId;
      dispatch({
        type: 'BLOCK_CREATED',
        blockType: 'post_videos',
        data: {
          assetId,
          status: 'uploading',
          isPlayable: false,
          isFullyProcessed: false,
          processingPercent: 0,
          caption: '',
        },
      });
      blockCreated = true;
      await uploadWithTus(file, data.uploadURL, () => {});

      dispatch({
        type: 'VIDEO_UPDATED_BY_ASSET',
        assetId,
        changes: { status: 'processing' },
      });
    } catch (error) {
      if (blockCreated) {
        dispatch({ type: 'VIDEO_REMOVED_BY_ASSET', assetId });
      }
      if (assetId) GalleryService.deletePostVideoUpload(assetId).catch(() => {});
      console.error('[BoardVideo] Upload failed', error);
      alert('Something went wrong uploading your video.');
    }
  };

  useEffect(() => {
    setTriggerVideoUpload(() => () => inputRef.current?.click());
  }, [setTriggerVideoUpload]);

  return (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED_VIDEO_TYPES.join(',')}
      hidden
      onChange={event => {
        const [file] = event.target.files || [];
        event.target.value = '';
        uploadVideo(file);
      }}
    />
  );
}
